import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { getApiUrl } from '../config';

interface TokenData {
  matchId: string;
  match: {
    id: string;
    date: string;
    time: string;
    location: string;
    status: string;
  };
  targetPlayer: { id: string; name: string; position: string; isSubstitute: boolean };
  claimed: boolean;
  playerStillActive: boolean;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const ReplacementClaimPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TokenData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [success, setSuccess] = useState(false);

  const isLoggedIn = !!localStorage.getItem('authToken');

  useEffect(() => {
    if (!token) return;
    fetch(getApiUrl(`/api/replacement-tokens/${token}`))
      .then((r) => r.json())
      .then((json) => {
        if (json.error) setError(json.error);
        else setData(json);
      })
      .catch(() => setError('Error al cargar el link'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleClaim = async () => {
    if (!isLoggedIn) {
      localStorage.setItem('postLoginRedirect', `/reemplazo/${token}`);
      navigate('/');
      return;
    }
    setClaiming(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/api/replacement-tokens/${token}/claim`), {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'No se pudo tomar el lugar');
      } else {
        setSuccess(true);
        setTimeout(() => navigate(`/match/${data?.matchId}`), 2000);
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setClaiming(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: theme.colors.background,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <p style={{ color: theme.colors.textSecondary }}>Cargando...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontFamily: theme.fonts.heading, margin: '0 0 8px 0' }}>Link inválido</h2>
        <p style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  if (!data) return null;

  if (data.claimed) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontFamily: theme.fonts.heading, margin: '0 0 8px 0' }}>Lugar ya tomado</h2>
        <p style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
          Este lugar ya fue tomado por otro jugador.
        </p>
      </div>
    );
  }

  if (!data.playerStillActive) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
        <h2 style={{ fontFamily: theme.fonts.heading, margin: '0 0 8px 0' }}>Lugar no disponible</h2>
        <p style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
          Este lugar ya no está disponible en el partido.
        </p>
      </div>
    );
  }

  if (success) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontFamily: theme.fonts.heading, margin: '0 0 8px 0' }}>¡Te sumaste!</h2>
        <p style={{ color: theme.colors.textSecondary }}>Redirigiendo al partido...</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: theme.colors.surface,
          borderRadius: 16,
          border: `1px solid ${theme.colors.border}`,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <h2 style={{ fontFamily: theme.fonts.heading, margin: 0, textAlign: 'center' }}>
          ¿Querés tomar este lugar?
        </h2>

        {/* Match info */}
        <div
          style={{
            background: theme.colors.background,
            borderRadius: 10,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontSize: 13, color: theme.colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            El partido
          </div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{data.match.location}</div>
          <div style={{ fontSize: 14, color: theme.colors.textSecondary }}>
            {data.match.date} · {data.match.time}
          </div>
        </div>

        {/* Player being replaced */}
        <div
          style={{
            background: theme.colors.background,
            borderRadius: 10,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ fontSize: 13, color: theme.colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Reemplazás a
          </div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{data.targetPlayer.name}</div>
          <div style={{ fontSize: 13, color: theme.colors.textSecondary }}>
            {data.targetPlayer.position}{data.targetPlayer.isSubstitute ? ' · Suplente' : ' · Titular'}
          </div>
        </div>

        {error && (
          <p style={{ color: theme.colors.danger || '#ef4444', fontSize: 13, margin: 0, textAlign: 'center' }}>{error}</p>
        )}

        <button
          type="button"
          onClick={handleClaim}
          disabled={claiming}
          style={{
            padding: '14px',
            background: theme.colors.primary,
            color: '#000',
            border: 'none',
            borderRadius: 10,
            fontWeight: 800,
            fontSize: 16,
            cursor: claiming ? 'not-allowed' : 'pointer',
            opacity: claiming ? 0.7 : 1,
            fontFamily: theme.fonts.heading,
          }}
        >
          {claiming ? 'Procesando...' : isLoggedIn ? '⚽ Tomar el lugar' : '🔑 Iniciar sesión para unirme'}
        </button>

        <p style={{ fontSize: 12, color: theme.colors.textSecondary, margin: 0, textAlign: 'center' }}>
          Al tomar el lugar, pasás a ser parte del partido sin necesitar aprobación del organizador.
        </p>
      </div>
    </div>
  );
};

export default ReplacementClaimPage;
